import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import SelectField from 'shared/components/formFields/select-field';
import ViewingModeController from 'shared/components/viewing-mode-controller/viewing-mode-controller';

import { useDeviceDetailsHook } from 'features/device/device-details/hooks/device-details-hook';
import { DomipEventType } from 'features/device/domain/models/domip-event-type';
import styles from './domip-input-settings.module.scss';

const DomipInputSettings = (): React.JSX.Element => {
  const { t } = useTranslation('deviceDetails');
  const { t: tIOEventTypes } = useTranslation('ioEventTypes');
  const { currentDevice, currentViewingMode } = useDeviceDetailsHook();
  const { control } = useFormContext();

  const options = Object.values(DomipEventType)
    .map((eventType) => ({ eventType, translated: tIOEventTypes(eventType) }))
    .sort((a, b) => a.translated.localeCompare(b.translated));

  const selectField = (label: string, name: string): React.JSX.Element => (
    <ViewingModeController
      viewingMode={currentViewingMode}
      name={name}
      control={control}
      label={label}
      render={({ field }) => (
        <SelectField
          {...field}
          value={field.value ?? DomipEventType.NoEvent}
          onChange={(event) => field.onChange(event.target.value)}
          id={name}
          label={label}
          variant="outlined"
          className="form-field"
          data-testid={`domipInputEvent${name}Select`}
        >
          {options.map(({ eventType, translated }) => (
            <MenuItem key={eventType} value={eventType}>
              {translated}
            </MenuItem>
          ))}
        </SelectField>
      )}
    />
  );

  const getView = (): React.JSX.Element =>
    currentViewingMode === 'viewing' ? (
      <div className={styles.viewingContainer}>
        <Typography variant="subtitle1">{`${t('info.eventTypeLow')}: ${currentDevice?.domipEvent?.eventTypeLow ? tIOEventTypes(currentDevice?.domipEvent?.eventTypeLow) : '-'}`}</Typography>
        <Typography variant="subtitle1">{`${t('info.eventTypeHigh')}: ${currentDevice?.domipEvent?.eventTypeHigh ? tIOEventTypes(currentDevice?.domipEvent?.eventTypeHigh) : '-'}`}</Typography>
      </div>
    ) : (
      <div className={styles.selectContainer}>
        {selectField(t('info.eventTypeLow'), 'eventTypeLow')}
        {selectField(t('info.eventTypeHigh'), 'eventTypeHigh')}
      </div>
    );

  return (
    <div className="settings-item full-width" data-testid="deviceEventsContainer">
      <Typography variant="h5">{t('info.deviceSettingsLabel')}</Typography>
      <div className="settings-item-content full-width">{getView()}</div>
    </div>
  );
};

export default DomipInputSettings;
